import type { FC, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { useFocusTrap } from "@ui/hooks/useFocusTrap";
import { useSwipeBack } from "@ui/hooks/useSwipeBack";
import classNames from "classnames";

import { useLogOut } from "../../hooks/useLogOut";

import { AccountActionsPanel } from "./components/AccountActionsPanel";
import { LanguageNavPanel } from "./components/LanguageNavPanel";
import { NavLinksPanel } from "./components/NavLinksPanel";
import { NavPanel } from "./components/NavPanel";
import { SubmenuPanel } from "./components/SubmenuPanel";
import { ToolsPanel } from "./components/ToolsPanel";

import type { MobileNavigationProps } from "./types";

import styles from "./MobileNavigation.module.scss";

const ROOT_PANEL_ID = "mobile-nav-level1";
const LANGUAGE_PANEL_ID = "mobile-nav-language";
const ACCOUNT_PANEL_ID = "mobile-nav-account";

const getSubmenuPanelId = (id: string) => `mobile-nav-level2-${id}`;

type PendingFocus = { type: "panel" };

type OpenPanel = { panelId: string; trigger: HTMLElement | null };

const focusWithoutRing = (element?: HTMLElement | null) => {
  element?.focus({ focusVisible: false, preventScroll: true });
};

export const MobileNavigation: FC<MobileNavigationProps> = ({
  isOpen,
  onClose,
  navLinks,
  toolsLinks,
  languages,
  isUserLoggedIn,
  logoutLabel,
  onLogOut,
  navLabel,
  closeLabel,
  languageLabel,
  searchLabel,
  isSearchOpened,
  onOpenSearch,
  onCloseSearch,
  renderSubmenu,
  cartLabel,
  cartItemsCount,
  onCartOpen,
  accountDropdownSettings,
  loyaltyPointsCurrency,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const panelRefs = useRef<Map<string, HTMLElement>>(new Map());
  const pendingFocusRef = useRef<PendingFocus | null>(null);
  const [stack, setStack] = useState<OpenPanel[]>([]);
  const [isResettingStack, setIsResettingStack] = useState(false);
  const activePanelId = stack.length ? stack[stack.length - 1].panelId : ROOT_PANEL_ID;
  const [isLogOutPending, setIsLogOutPending] = useState(false);
  const handleLogOut = useLogOut(onLogOut, setIsLogOutPending);

  useFocusTrap({ isActive: isOpen, containerRef: navRef, mountedSignal: activePanelId });

  const registerPanel = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) {
        panelRefs.current.set(id, node);
      } else {
        panelRefs.current.delete(id);
      }
    },
    []
  );

  const openPanel = (panelId: string) => (event: MouseEvent<HTMLButtonElement>) => {
    pendingFocusRef.current = { type: "panel" };
    setStack((prev) => [...prev, { panelId, trigger: event.currentTarget }]);
  };

  const goBack = useCallback(() => {
    let triggerToFocus: HTMLElement | null = null;

    flushSync(() => {
      setStack((prev) => {
        if (!prev.length) return prev;
        const activeLevel = prev[prev.length - 1];
        triggerToFocus =
          document.querySelector<HTMLElement>(`button[aria-controls="${activeLevel.panelId}"]`) ?? activeLevel.trigger;
        return prev.slice(0, -1);
      });
    });

    (triggerToFocus as HTMLElement | null)?.focus({ preventScroll: true });
  }, []);

  useSwipeBack({ ref: navRef, enabled: isOpen && stack.length > 0, onSwipeBack: goBack });

  // Keep the whole nav out of the tab and a11y trees while collapsed.
  useEffect(() => {
    const node = navRef.current;
    if (!node) return;

    if (isOpen) {
      node.removeAttribute("inert");
    } else {
      node.setAttribute("inert", "");
    }
  }, [isOpen]);

  // Reset to the root panel when opening, so close animation does not visibly change levels.
  useEffect(() => {
    if (isOpen) {
      setIsResettingStack(true);
      setStack([]);
    }
  }, [isOpen]);

  // Keep transitions disabled for exactly one render after stack reset.
  useEffect(() => {
    if (isResettingStack) {
      setIsResettingStack(false);
    }
  }, [isResettingStack]);

  // Move focus when the active panel changes: to the panel on open, to the trigger on back.
  useLayoutEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    pendingFocusRef.current = null;

    focusWithoutRing(panelRefs.current.get(activePanelId));
  }, [activePanelId]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();

    if (stack.length) {
      goBack();
    } else {
      onClose();
    }
  };

  return (
    <div
      id="mobile-navigation"
      className={classNames(styles.root, { [styles.open]: isOpen })}
      data-testid="mobile-navigation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <nav ref={navRef} className={styles.nav} aria-label={navLabel} onKeyDown={handleKeyDown}>
        <NavPanel
          id={ROOT_PANEL_ID}
          isActive={activePanelId === ROOT_PANEL_ID}
          isRoot
          disableTransition={isResettingStack}
          ref={registerPanel(ROOT_PANEL_ID)}
        >
          <button type="button" className={styles.visuallyHidden} onClick={onClose}>
            {closeLabel}
          </button>
          <NavLinksPanel
            navLinks={navLinks}
            activePanelId={activePanelId}
            accountPanelId={ACCOUNT_PANEL_ID}
            loyaltyPointsCurrency={loyaltyPointsCurrency}
            accountDropdownSettings={accountDropdownSettings}
            getSubmenuPanelId={getSubmenuPanelId}
            openPanel={openPanel}
            renderSubmenu={renderSubmenu}
          />
          <ToolsPanel
            toolsLinks={toolsLinks}
            activePanelId={activePanelId}
            languagePanelId={LANGUAGE_PANEL_ID}
            openPanel={openPanel}
            isUserLoggedIn={isUserLoggedIn}
            logoutLabel={logoutLabel}
            onLogOut={handleLogOut}
            isLogOutPending={isLogOutPending}
            languageLabel={languageLabel}
            searchLabel={searchLabel}
            isSearchOpened={isSearchOpened}
            onOpenSearch={onOpenSearch}
            onCloseSearch={onCloseSearch}
            cartLabel={cartLabel}
            cartItemsCount={cartItemsCount}
            onCartOpen={onCartOpen}
            showLanguageSelector={languages.length > 1}
          />
        </NavPanel>

        {navLinks.map((link) => {
          if (!link.secondaryNavigation || !renderSubmenu) return null;

          const panelId = getSubmenuPanelId(link.secondaryNavigation.id);

          return (
            <SubmenuPanel
              key={panelId}
              panelId={panelId}
              isActive={activePanelId === panelId}
              title={link.title}
              onBack={goBack}
              disableTransition={isResettingStack}
              panelRef={registerPanel(panelId)}
            >
              {renderSubmenu(link.secondaryNavigation.id)}
            </SubmenuPanel>
          );
        })}

        {languages.length > 1 && (
          <LanguageNavPanel
            panelId={LANGUAGE_PANEL_ID}
            isActive={activePanelId === LANGUAGE_PANEL_ID}
            languageLabel={languageLabel}
            languages={languages}
            onBack={goBack}
            disableTransition={isResettingStack}
            panelRef={registerPanel(LANGUAGE_PANEL_ID)}
          />
        )}

        {isUserLoggedIn && accountDropdownSettings && (
          <AccountActionsPanel
            panelId={ACCOUNT_PANEL_ID}
            isActive={activePanelId === ACCOUNT_PANEL_ID}
            accountDropdownSettings={accountDropdownSettings}
            onBack={goBack}
            onLogOut={handleLogOut}
            isLogOutPending={isLogOutPending}
            logoutLabel={logoutLabel}
            panelRef={registerPanel(ACCOUNT_PANEL_ID)}
          />
        )}
      </nav>
    </div>
  );
};
